import DatesService from 'common/services/Dates';
import { hourFr, hourRangeFr } from '../ui/format';

/**
 * Formatage de l'état d'ouverture d'un salon.
 *
 * La maquette montre trois formulations de fermeture (piège n°5) :
 *   « Fermé. Ouvrira demain à 9h30 »
 *   « Fermé. Ouvrira mardi à 9h00 »
 *   « Fermé. Ouvrira à 9h30 »
 * Un seul formateur, trois branches : plus tard aujourd'hui → sans jour,
 * demain → « demain », au-delà → le nom du jour.
 *
 * Quatre sources se superposent, dans cet ordre de priorité :
 *   1. `forcedCloseUntil` — fermeture exceptionnelle jusqu'à une date ;
 *   2. `forcedOpenUntil`  — ouverture exceptionnelle jusqu'à une date ;
 *   3. `additionalDays`   — horaires dérogatoires pour une date précise ;
 *   4. `workingDaysPerWeek` — la semaine type.
 *
 * `nonWorkingDays` et `open77` sont volontairement ignorés : le backend force
 * le premier à `[]` (`models/services/places_manager.js:278` et `:348`) et le
 * second n'est consommé par aucun template ni par `common`.
 *
 * L'ouverture est calculée ici et non par `DatesService.storeIsOpen`, qui a deux
 * défauts non corrigeables sans changer le rendu de 2025_VOG (cf. corollaire de
 * la décision #14 du PLAN.md) : son horloge est figée à l'import du module
 * (`Dates.js:14-16`), et `isInRange` compare en strict (`Dates.js:131`) — un
 * salon à l'heure d'ouverture pile y est fermé. Seul `filterAdditionalDay`, qui
 * n'a ni l'un ni l'autre, est réutilisé.
 *
 * **L'horloge est celle du salon, pas celle de la machine ni du visiteur.** Le module est
 * évalué deux fois pour une même page : côté serveur au `renderToString`, côté
 * navigateur à l'hydratation. Le conteneur SSR tourne en UTC, le visiteur en
 * `Europe/Paris` — avec `new Date().getHours()`, les deux passes n'étaient pas
 * du même côté de la frontière d'ouverture deux heures par jour (l'été), et
 * React 17 repatche bien le texte à l'hydratation mais **pas** les attributs :
 * la puce affichait « Ouvert · Ferme à 19h30 » avec la classe `closed`, donc en
 * rouge. Tout ce qui dépend de « maintenant » passe donc par `zonedNow`, qui
 * lit l'heure dans le fuseau du salon quel que soit celui de l'hôte. Corollaire :
 * le HTML servi aux moteurs annonce enfin le bon état, ce que l'heure UTC lui
 * interdisait.
 */

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DAY_NAMES_FR = {
  monday: 'lundi',
  tuesday: 'mardi',
  wednesday: 'mercredi',
  thursday: 'jeudi',
  friday: 'vendredi',
  saturday: 'samedi',
  sunday: 'dimanche',
};

const WEEK_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const SHORT_NAMES = {
  monday: 'Lun',
  tuesday: 'Mar',
  wednesday: 'Mer',
  thursday: 'Jeu',
  friday: 'Ven',
  saturday: 'Sam',
  sunday: 'Dim',
};

const toMinutes = (hour) => {
  const [h, m] = String(hour || '').split(':');
  return parseInt(h, 10) * 60 + parseInt(m || '0', 10);
};

const DEFAULT_TIMEZONE = 'Europe/Paris';

/**
 * Fuseau du salon déduit du code postal — aucune donnée de fuseau ne remonte du
 * back, ni sur le salon ni sur la marque, mais le plan de numérotation postale
 * des collectivités d'outre-mer est sans ambiguïté sur ses trois premiers
 * chiffres. Le réseau en compte deux en préprod (Le Tampon, `97430`, et
 * Fort-de-France, `97200`) : à Paris ils seraient annoncés fermés jusqu'à deux
 * heures après leur ouverture réelle.
 *
 * Le fuseau qui compte est celui du **salon**, pas celui du visiteur : des
 * horaires d'ouverture se lisent sur place. Un métropolitain qui consulte un
 * salon réunionnais depuis Paris doit lire l'état du salon à La Réunion.
 */
const ZIP_TIMEZONES = {
  971: 'America/Guadeloupe',
  972: 'America/Martinique',
  973: 'America/Cayenne',
  974: 'Indian/Reunion',
  975: 'America/Miquelon',
  976: 'Indian/Mayotte',
  977: 'America/Guadeloupe', // Saint-Barthélemy
  978: 'America/Guadeloupe', // Saint-Martin
  986: 'Pacific/Wallis',
  987: 'Pacific/Tahiti',
  988: 'Pacific/Noumea',
};

export const timeZoneForAddress = (address) => {
  const zip = String((address && address.zip) || '').replace(/\s/g, '');
  return ZIP_TIMEZONES[zip.slice(0, 3)] || DEFAULT_TIMEZONE;
};

const WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/**
 * Un formateur par fuseau, mémoïsé : `Intl.DateTimeFormat` est coûteux à
 * construire et le listing appelle le calcul une fois par salon.
 * `null` si l'ICU de l'hôte ignore le fuseau — on retombe alors sur l'heure
 * locale de la machine, c'est-à-dire sur le comportement d'avant.
 */
const formatters = {};

const zonedFormatter = (timeZone) => {
  if (!Object.prototype.hasOwnProperty.call(formatters, timeZone)) {
    try {
      formatters[timeZone] = new Intl.DateTimeFormat('en-US', {
        timeZone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch (error) {
      formatters[timeZone] = null;
    }
  }
  return formatters[timeZone];
};

/** @returns {{ dayIndex: number, minutes: number }} la date lue dans `timeZone`. */
const zonedParts = (date, timeZone) => {
  const formatter = zonedFormatter(timeZone);
  if (!formatter) {
    return { dayIndex: date.getDay(), minutes: date.getHours() * 60 + date.getMinutes() };
  }

  const parts = {};
  formatter.formatToParts(date).forEach((part) => {
    parts[part.type] = part.value;
  });

  // `hour12: false` rend « 24 » à minuit sur certains moteurs.
  const hour = parseInt(parts.hour, 10) % 24;
  return {
    dayIndex: WEEKDAY_INDEX[parts.weekday],
    minutes: hour * 60 + parseInt(parts.minute, 10),
  };
};

const zonedNow = (timeZone) => zonedParts(new Date(), timeZone);

const zonedDayKey = (date, timeZone) => DAY_KEYS[zonedParts(date, timeZone).dayIndex];

/** Date exploitable, ou `null` — les dates viennent de Mongo en chaîne libre. */
const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

const isFuture = (value) => {
  const date = parseDate(value);
  return !!date && date.getTime() > Date.now();
};

const dateFr = (value) =>
  DatesService.toLocaleDateString(value, 'fr-FR', { day: 'numeric', month: 'long' });

/**
 * Jours dérogatoires applicables aux 7 lignes de la grille, indexés par jour.
 *
 * La fenêtre est volontairement aujourd'hui → J+6 : c'est exactement la portée
 * de la grille, donc la correspondance date → ligne est bijective. Avec la
 * fenêtre par défaut de `filterAdditionalDay` (30 jours), une date à trois
 * semaines écraserait la ligne du même jour de la semaine en cours.
 *
 * Une entrée à `hours: []` est un jour de fermeture exceptionnelle — d'où la
 * distinction entre « pas d'entrée » et « entrée vide » chez les appelants.
 */
const exceptionsByDay = (workingTime, timeZone) => {
  const additionalDays = (workingTime && workingTime.additionalDays) || [];
  const result = {};

  additionalDays.forEach((additionalDay) => {
    if (!additionalDay || !additionalDay.day) return;
    const date = parseDate(additionalDay.day.date);
    if (!date) return;

    let inWindow = false;
    try {
      inWindow = DatesService.filterAdditionalDay(additionalDay, 6);
    } catch (error) {
      inWindow = false;
    }
    if (!inWindow) return;

    result[zonedDayKey(date, timeZone)] = {
      hours: additionalDay.hours || [],
      desc: additionalDay.day.desc || '',
    };
  });

  return result;
};

const slotsFor = (workingTime, dayKey, exceptions = {}) => {
  if (Object.prototype.hasOwnProperty.call(exceptions, dayKey)) {
    return exceptions[dayKey].hours;
  }
  return ((workingTime && workingTime.workingDaysPerWeek) || {})[dayKey] || [];
};

/** Créneau en cours aujourd'hui, s'il y en a un. */
const currentSlot = (workingTime, exceptions, timeZone) => {
  const { dayIndex, minutes } = zonedNow(timeZone);
  return slotsFor(workingTime, DAY_KEYS[dayIndex], exceptions).find(
    (slot) => toMinutes(slot.begin) <= minutes && minutes < toMinutes(slot.end),
  );
};

/** Première ouverture à venir, en balayant au plus 7 jours. */
const nextOpening = (workingTime, exceptions, timeZone) => {
  const { dayIndex: todayIndex, minutes } = zonedNow(timeZone);

  for (let offset = 0; offset < 8; offset += 1) {
    const dayKey = DAY_KEYS[(todayIndex + offset) % 7];
    const slots = slotsFor(workingTime, dayKey, exceptions)
      .slice()
      .sort((a, b) => toMinutes(a.begin) - toMinutes(b.begin));

    const slot = slots.find((candidate) => offset > 0 || toMinutes(candidate.begin) > minutes);
    if (slot) return { offset, dayKey, begin: slot.begin };
  }
  return null;
};

/**
 * @returns {{ isOpen: boolean, label: string, title: string }} — `label` vide si
 * les horaires sont inconnus ; l'appelant n'affiche alors aucune puce. `title`
 * porte le motif de la dérogation quand il y en a un (la maquette n'a pas de
 * place pour l'afficher en clair).
 */
export const getOpeningStatus = (workingTime, timeZone = DEFAULT_TIMEZONE) => {
  if (!workingTime || !workingTime.workingDaysPerWeek) {
    return { isOpen: false, label: '', title: '' };
  }

  // La fermeture forcée prime sur l'ouverture forcée : annoncer ouvert un salon
  // fermé est la pire des deux erreurs.
  const forcedClose = workingTime.forcedCloseUntil || {};
  if (isFuture(forcedClose.date)) {
    return {
      isOpen: false,
      label: `Fermé. Ouvrira le ${dateFr(forcedClose.date)}`,
      title: forcedClose.desc || '',
    };
  }

  const forcedOpen = workingTime.forcedOpenUntil || {};
  if (isFuture(forcedOpen.date)) {
    // Pas de « Ferme à … » : une ouverture forcée n'a pas de créneau.
    return { isOpen: true, label: 'Ouvert', title: forcedOpen.desc || '' };
  }

  const exceptions = exceptionsByDay(workingTime, timeZone);
  const todayException = exceptions[DAY_KEYS[zonedNow(timeZone).dayIndex]];
  const title = (todayException && todayException.desc) || '';

  const slot = currentSlot(workingTime, exceptions, timeZone);
  if (slot) {
    return { isOpen: true, label: `Ouvert · Ferme à ${hourFr(slot.end)}`, title };
  }

  const next = nextOpening(workingTime, exceptions, timeZone);
  if (!next) return { isOpen: false, label: 'Fermé', title };

  const when = next.offset === 0 ? '' : next.offset === 1 ? 'demain ' : `${DAY_NAMES_FR[next.dayKey]} `;
  return { isOpen: false, label: `Fermé. Ouvrira ${when}à ${hourFr(next.begin)}`, title };
};

/**
 * Les 7 lignes du tableau d'horaires du panneau identité, aujourd'hui marqué.
 *
 * Retourne `[]` quand les horaires sont inconnus, pour que `OpeningHoursGrid`
 * masque le bloc au lieu d'afficher sept « Fermé ».
 */
export const getWeekSchedule = (workingTime, timeZone = DEFAULT_TIMEZONE) => {
  if (!workingTime || !workingTime.workingDaysPerWeek) return [];

  const todayKey = DAY_KEYS[zonedNow(timeZone).dayIndex];
  const exceptions = exceptionsByDay(workingTime, timeZone);

  return WEEK_ORDER.map((dayKey) => {
    const slots = slotsFor(workingTime, dayKey, exceptions);
    const exception = exceptions[dayKey];

    return {
      key: dayKey,
      shortName: SHORT_NAMES[dayKey],
      fullName: DAY_NAMES_FR[dayKey],
      isToday: dayKey === todayKey,
      isException: !!exception,
      exceptionDesc: (exception && exception.desc) || '',
      // Conservé pour les consommateurs qui veulent une chaîne unique ; la
      // grille, elle, empile les créneaux et lit `slots`.
      hours: slots.length ? slots.map((slot) => hourRangeFr(slot.begin, slot.end)).join(' · ') : 'Fermé',
      slots,
    };
  });
};

export default { getOpeningStatus, getWeekSchedule, timeZoneForAddress };
